import React, { PropsWithChildren, RefObject, useContext, useRef } from "react";
import { useTranslation } from "i18n";
import cx from "classnames";
import { useOnClickOutside } from "usehooks-ts";
import { oidcLogout, useAuth } from "shared-auth";
import Avatar from "../Avatar";
import { navItemStateBaseClass, transitionBaseClass } from "../../constants";
import NavThemeContext, { ThemeContextValues } from "../NavThemeContext";
import { ReactComponent as CloseIcon } from "../../../../../images/svg/icons/cross-thick.svg";
import { ReactComponent as SignoutIcon } from "../../../../../images/svg/icons/action-sign-out.svg";

interface Props {
  visible: boolean;
  toggle: () => void;
  userMenuButtonRef: RefObject<HTMLButtonElement>;
  avatar?: string;
}

const UserMenu = ({
  visible,
  toggle,
  avatar,
  userMenuButtonRef,
  children,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const theme = useContext(NavThemeContext);
  const auth = useAuth();
  const ref = useRef(null);
  useOnClickOutside([ref, userMenuButtonRef], () => {
    if (visible) toggle();
  });

  const className = cx(
    "flex flex-col fixed w-full md:w-[320px] right-0 bg-white z-[4]",
    "max-h-[calc(100vh_-_90px)] overflow-auto",
    transitionBaseClass,
    {
      "translate-x-0 shadow-[0px_8px_13px_0px_rgba(0,0,0,0.3)]": visible,
      "translate-x-full": !visible,
    }
  );

  const borderColor = {
    "border-color-user-cd": theme === ThemeContextValues.Cd,
    "border-color-user-agent": theme === ThemeContextValues.Agent,
    "border-color-user-performer": theme === ThemeContextValues.Performer,
    "border-color-user-filmmakers": theme === ThemeContextValues.Filmmakers,
    "border-color-neutral-two": theme === ThemeContextValues.Default,
  };

  return (
    <div className={className} ref={ref}>
      <div
        className={cx("flex items-center gap-3 border-b-2 p-2.5", borderColor)}
      >
        <div className="h-8 w-8 shrink-0">
          <Avatar img={avatar} />
        </div>
        <span className="text-m font-semibold uppercase">
          {auth.user?.profile?.name}
        </span>
        <button
          className="text-color-neutral-two ml-auto"
          onClick={toggle}
          aria-label={t("common:aria.label.closeUserMenu")}
        >
          <CloseIcon aria-hidden="true" focusable="false" />
        </button>
      </div>
      {children}
      <button
        className={cx(
          "text-s mt-auto flex items-center gap-2.5 px-2.5 py-5 font-semibold",
          navItemStateBaseClass
        )}
        onClick={oidcLogout}
      >
        <SignoutIcon />
        {t("common:nav.signout")}
      </button>
    </div>
  );
};

export default UserMenu;
